import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import {
  Estimate,
  Quote,
  VendorOrder,
} from '../../../../../../shared/models/vendor-order.model';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { QuoteCostCardComponent } from '../../quotes/quote-cost-card/quote-cost-card.component';
import {
  buildCostBreakdown,
  computeCostDeltas,
} from '../../quotes/quote-cost-card/quote-cost.util';

export type FinalCostDialogResult = 'reject' | boolean | undefined;

@Component({
  selector: 'app-review-final-cost-modal',
  imports: [QuoteCostCardComponent],
  templateUrl: './review-final-cost-modal.component.html',
  styleUrl: './review-final-cost-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewFinalCostModalComponent {
  readonly order = input.required<VendorOrder>();
  readonly estimate = input.required<Estimate>();
  readonly acceptedQuote = input.required<Quote>();
  readonly reanalysis = input.required<Quote>();

  readonly deliveryType = computed(() => {
    const estimate = this.estimate();
    if (estimate.rushedDateSelected === true) return 'Rushed';
    return estimate.quote?.rushed && !estimate.quote?.normal
      ? 'Rushed'
      : 'Standard';
  });

  readonly deliveryDate = computed(() => {
    const estimate = this.estimate();
    const raw =
      this.deliveryType() === 'Rushed'
        ? estimate.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
        : estimate.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;
    return this.formatDate(raw ?? estimate.deadline);
  });

  readonly quoteId = computed(() => this.order().jobNumber ?? '--');

  readonly currency = computed(() =>
    toIsoCurrency(
      this.reanalysis().currency ??
        this.acceptedQuote().currency ??
        this.estimate().currency,
    ),
  );

  readonly costBreakdown = computed(() =>
    buildCostBreakdown(this.reanalysis()),
  );

  readonly totalCost = computed(
    () =>
      this.reanalysis().totalCost ??
      this.costBreakdown().reduce((sum, item) => sum + item.cost, 0),
  );

  readonly costDeltas = computed<ReadonlyMap<string, number>>(() =>
    computeCostDeltas(this.acceptedQuote(), this.costBreakdown()),
  );

  private formatDate(raw: string | undefined): string {
    if (!raw) return '--';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }
}
